import { faPlus } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    setOnlineBookingStepThunk,
    submitOnlineBookingSelectPetsThunk,
    updateOnlineBookingPetThunk
} from 'actions/onlineBooking/OnlineBookingActions';
import { PoweredByCuddles } from 'components/Cuddles/PoweredBy/PoweredByCuddles';
import { LeftColumn } from 'components/Layout/TwoColumns/LeftColumn/LeftColumn';
import { RightColumn } from 'components/Layout/TwoColumns/RightColumn/RightColumn';
import { MarketplaceInfo } from 'components/Marketplace/Info/MarketplaceInfo';
import { OnlineBookingResume } from 'components/OnlineBooking/Resume/OnlineBookingResume';
import { PetForm } from 'components/Pet/Form/PetForm';
import { PetOption } from 'components/Pet/Option/PetOption';
import { Button } from 'components/UI/Button/Button';
import { useMarketplace } from 'hooks/useMarketplace';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getOnlineBookingState } from 'reducers/onlineBooking/OnlineBookingSelectors';
import { OnlineBookingStatus } from 'reducers/onlineBooking/OnlineBookingState';
import { OnlineBookingStep } from 'types/OnlineBooking';
import { Pet } from 'types/Pet';

import classes from './OnlineBookingSelectPetsStep.module.scss';

export const OnlineBookingSelectPetsStep: React.FC = () => {
    const dispatch = useDispatch();
    const marketplace = useMarketplace();

    const {
        pets,
        status,
        customer,
        bookingItems,
        isReturningClient,
        loading: isLoading
    } = useSelector(getOnlineBookingState);

    const [editingPetUuid, setEditingPetUuid] = React.useState<string | null>(null);
    const [selectedUuids, setSelectedUuids] = React.useState<Array<string>>(
        bookingItems.map(item => item.pet.uuid) || []
    );

    const petChangeHandler = (petUuid: string, checked: boolean) => {
        if (checked) {
            setSelectedUuids([...selectedUuids, petUuid]);
        } else {
            setSelectedUuids(selectedUuids.filter(uuid => uuid !== petUuid));
        }
    };

    const addPetHandler = () => {
        dispatch(setOnlineBookingStepThunk(OnlineBookingStep.AddPet));
    };

    const editPetHandler = (uuid: string) => {
        setEditingPetUuid(uuid);
    };

    const editedPetHandler = (pet: Pet) => {
        dispatch(updateOnlineBookingPetThunk(pet));
    };

    const submitHandler = () => {
        const selectedPets = pets.filter(pet => selectedUuids.includes(pet.uuid)) || [];

        dispatch(submitOnlineBookingSelectPetsThunk(selectedPets));
    };

    React.useEffect(() => {
        if (status === OnlineBookingStatus.UpdatePetSuccess) {
            setEditingPetUuid(null);
        }
    }, [status]);

    return (
        <>
            <LeftColumn>
                <>
                    <h1 className="h2 underlined w-full">
                        {isReturningClient ? `Welcome back ${customer?.name}` : 'Select your pets'}
                    </h1>

                    {pets.map(pet => (
                        <div key={pet.uuid} className="w-full">
                            {editingPetUuid === pet.uuid ? (
                                <PetForm
                                    pet={pet}
                                    showSubmitButton
                                    loading={isLoading}
                                    className={classes.editForm}
                                    submitButtonLabel="Update"
                                    onSubmit={editedPetHandler}
                                />
                            ) : (
                                <PetOption
                                    pet={pet}
                                    onEdit={() => editPetHandler(pet.uuid)}
                                    checked={selectedUuids.includes(pet.uuid)}
                                    onChange={checked => petChangeHandler(pet.uuid, checked)}
                                />
                            )}
                        </div>
                    ))}

                    <Button variant="link" className={classes.addPetButton} onClick={addPetHandler}>
                        <FontAwesomeIcon icon={faPlus} />

                        <span>Add new pet</span>
                    </Button>

                    <div className="mt-auto hide-on-mobile-and-tablet pt-6">
                        <PoweredByCuddles />
                    </div>
                </>
            </LeftColumn>

            <RightColumn
                poweredByCuddles
                footer={
                    <Button fullWidth disabled={selectedUuids.length === 0} onClick={submitHandler}>
                        Continue
                    </Button>
                }
            >
                <div className="flex flex-col gap-5 hide-on-mobile-and-tablet">
                    <MarketplaceInfo inverted marketplace={marketplace} />

                    <OnlineBookingResume />
                </div>
            </RightColumn>
        </>
    );
};
